import { MedplumClient } from "@medplum/core";
import { Encounter, QuestionnaireResponse } from "@medplum/fhirtypes";

import { Alert, Badge, Button, Divider, Flex, Group, Paper, Stack, Title } from "@mantine/core";
import { type UseFormReturnType } from "@mantine/form";

import { TextWrapper } from "@/components/TextWrapper";
import { DotPhraseRichTextField } from "@/dot-phrases";
import EncounterBilling from "@/encounters/components/EncounterBilling/EncounterBilling";
import {
  LABOR_AMEND_BIRTH_LOAD_FAILED_MESSAGE,
  LABOR_AMEND_BIRTH_LOAD_FAILED_TITLE,
  LABOR_AMEND_LABOR_STAGES_LOAD_FAILED_MESSAGE,
  LABOR_AMEND_LABOR_STAGES_LOAD_FAILED_TITLE,
  LABOR_AMEND_PENDING_WORK_LOST_MESSAGE,
  LABOR_AMEND_PENDING_WORK_LOST_TITLE,
  LABOR_AMEND_POST_EFFECTS_LOST_MESSAGE,
  LABOR_AMEND_POST_EFFECTS_LOST_TITLE,
} from "@/encounters/components/EncounterLabor/constants/labor-encounter-amend";
import { LaborEncounterLiveObservationsProvider } from "@/encounters/components/EncounterLabor/contexts/LaborEncounterLiveObservationsContext";
import EncounterLaborHotKeys from "@/encounters/components/EncounterLabor/EncounterLabor/LaborMenu/EncounterLaborHotKeys";
import EncounterLaborMenuButton from "@/encounters/components/EncounterLabor/EncounterLabor/LaborMenu/EncounterLaborMenuButton";
import { EncounterLaborQuickButtons } from "@/encounters/components/EncounterLabor/EncounterLabor/LaborMenu/EncounterLaborQuickButtons";
import { LaborAmendmentActionsBar } from "@/encounters/components/EncounterLabor/EncounterLaborAmend/LaborAmendmentActionsBar";
import LaborAmendPendingChangesBanner from "@/encounters/components/EncounterLabor/EncounterLaborAmend/LaborAmendPendingChangesBanner";
import { useLaborAmendmentActions } from "@/encounters/components/EncounterLabor/EncounterLaborAmend/useLaborAmendmentActions";
import PostpartumFlowsheetTabs from "@/encounters/components/EncounterLabor/EncounterLaborPostpartum/PostpartumFlowsheetTabs";
import EncounterLaborTable from "@/encounters/components/EncounterLabor/EncounterLaborTable";
import {
  type BirthObservationsLoadStatus,
  isLaborAmendSignAndLockReady,
  type LaborStagesLoadStatus,
} from "@/encounters/components/EncounterLabor/utils/labor-amend-sign-and-lock-ready";
import EncounterTabs from "@/encounters/components/EncounterTabs";
import { ProceduresTab } from "@/encounters/components/ProceduresTab";
import { Files } from "@/encounters/components/shared/Files";
import { DefaultEncounterDrawerFormsStack } from "@/encounters/constants/encounter-drawer-forms-stack";
import { useLaborAmendFlowsheetPersist } from "@/encounters/contexts/LaborAmendFlowsheetPersistContext";
import { EncounterAmendmentFormType } from "@/encounters/zod-schemas/encounter-answers";
import { usDateFormat } from "@/utils/datetime";

import AmendmentFormReason from "../../shared/EncounterAmendmentForm";

export interface EncounterLaborAmendContentProps {
  form: UseFormReturnType<EncounterAmendmentFormType>;
  clientId: string;
  encounterId: string;
  encounter: Encounter;
  isEncounterActive: boolean;
  template: string;
  stack: {
    open: (id: DefaultEncounterDrawerFormsStack.FILES) => void;
  };
  medplum: MedplumClient;
  onSignAndLock: (values: EncounterAmendmentFormType) => Promise<QuestionnaireResponse>;
  isEncounterDataReady: boolean;
  laborStagesLoadStatus: LaborStagesLoadStatus;
  questionnaireResponse: QuestionnaireResponse | undefined;
  birthObservationsLoadStatus: BirthObservationsLoadStatus;
  onStripPreviewLaborStages?: () => void;
}

export function EncounterLaborAmendContent({
  form,
  clientId,
  encounterId,
  encounter,
  isEncounterActive,
  template,
  stack,
  medplum,
  onSignAndLock,
  isEncounterDataReady,
  laborStagesLoadStatus,
  questionnaireResponse,
  birthObservationsLoadStatus,
  onStripPreviewLaborStages,
}: EncounterLaborAmendContentProps) {
  const {
    hasLocalAmendPreviews,
    hasLostPendingWorkAfterRefresh,
    hasLostPostAmendmentEffectsAfterRefresh,
  } = useLaborAmendFlowsheetPersist();
  const isSignAndLockReady = isLaborAmendSignAndLockReady({
    isEncounterDataReady,
    laborStagesLoadStatus,
    birthObservationsLoadStatus,
    hasLocalAmendPreviews,
  });

  const amendmentActions = useLaborAmendmentActions({
    form,
    clientId,
    encounterId,
    questionnaireResponse,
    isSignAndLockReady,
    onSignAndLock,
    onStripPreviewLaborStages,
  });

  return (
    <>
      <EncounterLaborHotKeys clientId={clientId} onRefresh={undefined} encounterId={encounterId} />
      <Stack py="lg">
        <Paper radius="lg">
          <Stack
            gap="md"
            justify="space-between"
            mx={{ base: "xs", md: "xl" }}
            pt={{ base: "xs", xs: "md", sm: "lg", md: "xl" }}
            pb={{ base: "xs", xs: "md", sm: "lg", md: "xl" }}
          >
            <Group wrap="wrap" align="flex-start" justify="space-between">
              <Stack gap="xs">
                <Title order={2}>Amend Labor & Delivery Encounter</Title>
                <TextWrapper c="dimmed">
                  Service Date:{" "}
                  {encounter.period?.start ? usDateFormat(encounter.period.start) : ""}
                </TextWrapper>
                <TextWrapper c="dimmed">Template: {template}</TextWrapper>
              </Stack>
              <Group gap="sm" wrap="wrap" align="center">
                <Button
                  variant="outline"
                  onClick={amendmentActions.handleCancel}
                  data-testid="labor-amend-exit-amendment-view-header"
                  disabled={
                    amendmentActions.isSigningAndLocking || amendmentActions.isCancelDisabled
                  }
                >
                  Exit Amendment View
                </Button>
                <Badge color="red" variant="light">
                  Amendment Mode
                </Badge>
              </Group>
            </Group>
            <LaborAmendmentActionsBar
              {...amendmentActions}
              showCancel={false}
              testIdSuffix="header"
            />
          </Stack>
        </Paper>

        <AmendmentFormReason form={form} />

        <Paper radius="lg">
          <Stack
            justify="space-between"
            mx={{ base: "xs", md: "xl" }}
            pt={{ base: "xs", xs: "md", sm: "lg", md: "xl" }}
            pb={{ base: "xs", xs: "md", sm: "lg", md: "xl" }}
          >
            <Title order={3}>Encounter Note</Title>
            <DotPhraseRichTextField
              data-testid="encounter-note"
              value={form.values.encounterNote || ""}
              onChange={(value) => form.setFieldValue("encounterNote", value)}
            />
          </Stack>
        </Paper>

        <EncounterTabs
          form={form}
          isAmendment
          isEncounterActive={isEncounterActive}
          billingChildren={<EncounterBilling />}
          proceduresChildren={<ProceduresTab isAmendment isEncounterActive={isEncounterActive} />}
          objectiveChildren={
            <LaborEncounterLiveObservationsProvider
              clientId={clientId}
              encounterId={encounterId}
              skipInitialRefresh={
                laborStagesLoadStatus !== "ready" || birthObservationsLoadStatus !== "ready"
              }
            >
              <Stack py="xl" gap="xl">
                {laborStagesLoadStatus === "failed" && (
                  <Alert
                    color="red"
                    title={LABOR_AMEND_LABOR_STAGES_LOAD_FAILED_TITLE}
                    data-testid="labor-amend-labor-stages-failed-alert"
                  >
                    {LABOR_AMEND_LABOR_STAGES_LOAD_FAILED_MESSAGE}
                  </Alert>
                )}
                {birthObservationsLoadStatus === "failed" && (
                  <Alert
                    color="orange"
                    title={LABOR_AMEND_BIRTH_LOAD_FAILED_TITLE}
                    data-testid="labor-amend-birth-load-failed-alert"
                  >
                    {LABOR_AMEND_BIRTH_LOAD_FAILED_MESSAGE}
                  </Alert>
                )}
                {hasLostPendingWorkAfterRefresh && (
                  <Alert
                    color="orange"
                    title={LABOR_AMEND_PENDING_WORK_LOST_TITLE}
                    data-testid="labor-amend-pending-work-lost-alert"
                  >
                    {LABOR_AMEND_PENDING_WORK_LOST_MESSAGE}
                  </Alert>
                )}
                {hasLostPostAmendmentEffectsAfterRefresh && (
                  <Alert
                    color="red"
                    title={LABOR_AMEND_POST_EFFECTS_LOST_TITLE}
                    data-testid="labor-amend-post-effects-lost-alert"
                  >
                    {LABOR_AMEND_POST_EFFECTS_LOST_MESSAGE}
                  </Alert>
                )}
                <LaborAmendPendingChangesBanner />
                <Paper radius="lg">
                  <Stack
                    justify="space-between"
                    mx={{ base: "xs", md: "xl" }}
                    pt={{ base: "xs", xs: "md", sm: "lg", md: "xl" }}
                    pb={{ base: "xs", xs: "md", sm: "lg", md: "xl" }}
                  >
                    <Title order={3}>Client Summary & Instructions</Title>
                    <TextWrapper size="sm" c="dimmed">
                      The client will be notified of any changes made
                    </TextWrapper>
                    <DotPhraseRichTextField
                      data-testid="encounter-client-summary-editor"
                      value={form.values.encounterClientSummary || ""}
                      onChange={(value) => form.setFieldValue("encounterClientSummary", value)}
                    />
                  </Stack>
                </Paper>
                <Paper w="97%" mx="auto" bg="gray.0">
                  <PostpartumFlowsheetTabs
                    isViewOnly={false}
                    clientId={clientId}
                    encounterId={encounterId}
                  />
                </Paper>
                <Paper px="sm" py="lg" w="97%" mx="auto" radius={0} bg="gray.0">
                  <Flex gap="md" direction="column">
                    <Flex align="center" justify="space-between">
                      <Title order={3}>Labor & Delivery Flow Sheet</Title>
                      <EncounterLaborMenuButton clientId={clientId} encounterId={encounterId} />
                    </Flex>
                    <EncounterLaborQuickButtons clientId={clientId} encounterId={encounterId} />
                  </Flex>
                  <Divider my="xl" style={{ border: "1px solid var(--mantine-color-cream-4)" }} />
                  <EncounterLaborTable isViewOnly={false} showNumbersOnly={false} />
                  <Stack pt="xl">
                    <Files
                      open={stack.open}
                      medplum={medplum}
                      isAmendment={true}
                      clientId={clientId}
                      isEncounterActive={false}
                      encounterId={encounterId}
                      filesDrawerId={DefaultEncounterDrawerFormsStack.FILES}
                    />
                  </Stack>
                </Paper>
              </Stack>
            </LaborEncounterLiveObservationsProvider>
          }
        />

        <Group p="md" justify="flex-end">
          <LaborAmendmentActionsBar {...amendmentActions} testIdSuffix="footer" />
        </Group>
      </Stack>
    </>
  );
}
